import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Sparkles, Loader2, Timer, Dumbbell, Flame, Zap } from "lucide-react";

const DURATIONS = [10, 15, 20, 30];

const muscleLabel = (m) =>
  ({
    chest: "Chest", back: "Back", shoulders: "Shoulders", biceps: "Biceps",
    triceps: "Triceps", forearms: "Forearms", core: "Core", quads: "Quads",
    hamstrings: "Hamstrings", glutes: "Glutes", calves: "Calves",
  }[m] || m);

export default function MicroWorkoutModal({ muscleGroup, onClose }) {
  const [duration, setDuration] = useState(15);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setPlan(null);
    try {
      const { data } = await api.post("/workouts/micro-workout", {
        muscle_group: muscleGroup,
        duration_min: duration,
      });
      setPlan(data.plan);
    } catch (err) {
      const msg = err.response?.data?.detail || "AI generation failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-trigger first generation on open
  useEffect(() => {
    if (muscleGroup) {
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muscleGroup]);

  // ESC closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!muscleGroup) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="micro-workout-modal"
    >
      <div
        className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 p-5 flex items-start justify-between gap-4 z-10">
          <div>
            <div className="label-eyebrow text-[#6B9BFF] mb-1 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> AI Micro-Workout
            </div>
            <h2 className="font-display text-2xl leading-none">
              {muscleLabel(muscleGroup)} · <span className="text-[#6B9BFF]">{duration} min</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white"
            data-testid="micro-workout-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* duration picker */}
        <div className="px-5 pt-5 pb-3">
          <div className="label-eyebrow text-white/50 mb-2">Duration</div>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDuration(d)}
                disabled={loading}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                  duration === d
                    ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/5"
                    : "border-white/10 text-white/60 hover:border-white/30"
                }`}
                data-testid={`micro-duration-${d}`}
              >
                {d} min
              </button>
            ))}
            <button
              onClick={generate}
              disabled={loading}
              className="ml-auto px-4 py-2 text-xs uppercase tracking-wider border border-white/20 text-white hover:border-[#6B9BFF] hover:text-[#6B9BFF] inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="micro-regenerate"
            >
              <Zap className="w-3 h-3" /> Regenerate
            </button>
          </div>
        </div>

        {/* body */}
        <div className="px-5 pb-6">
          {loading && (
            <div className="py-16 flex flex-col items-center gap-3 text-white/60" data-testid="micro-loading">
              <Loader2 className="w-8 h-8 animate-spin text-[#6B9BFF]" />
              <div className="text-sm">Building your micro-session…</div>
            </div>
          )}

          {!loading && error && (
            <div className="py-10 text-center text-sm text-white/60" data-testid="micro-error">
              <div className="text-[#FF3B30] font-bold mb-2">Couldn't generate</div>
              <div>{error}</div>
              <button
                onClick={generate}
                className="mt-4 px-4 py-2 border border-white/20 text-xs uppercase tracking-wider hover:border-[#6B9BFF] hover:text-[#6B9BFF]"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && plan && (
            <div data-testid="micro-plan">
              {plan.name && (
                <div className="font-display text-xl mb-1">{plan.name}</div>
              )}
              {plan.summary && (
                <p className="text-white/60 text-sm mb-5">{plan.summary}</p>
              )}
              <div className="space-y-3">
                {(plan.exercises || []).map((ex, i) => (
                  <article
                    key={`${ex.name}-${i}`}
                    className="border border-white/10 bg-[#121212] p-4 hover:border-[#6B9BFF]/40 transition-colors"
                    data-testid={`micro-exercise-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="font-display text-lg leading-tight">{ex.name}</div>
                        {ex.muscle_group && (
                          <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">
                            {muscleLabel(ex.muscle_group)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-[#6B9BFF] text-xs">
                        <Dumbbell className="w-3 h-3" />
                        <span className="font-bold">#{i + 1}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 text-xs text-white/70">
                      {ex.is_static_hold && ex.hold_seconds ? (
                        <span className="inline-flex items-center gap-1">
                          <Timer className="w-3 h-3 text-[#6B9BFF]" />
                          Hold {ex.hold_seconds}s
                        </span>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1">
                            <Flame className="w-3 h-3 text-[#6B9BFF]" />
                            {ex.sets} × {ex.reps}
                          </span>
                          {ex.rest_sec ? (
                            <span className="inline-flex items-center gap-1">
                              <Timer className="w-3 h-3 text-[#6B9BFF]" />
                              Rest {ex.rest_sec}s
                            </span>
                          ) : null}
                        </>
                      )}
                    </div>

                    {ex.notes && (
                      <div className="text-xs text-white/50 mt-3 italic">{ex.notes}</div>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-5 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="text-xs uppercase tracking-widest text-white/60 hover:text-white px-4 py-2"
            data-testid="micro-workout-done"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
